import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { PageWrapper, Card, Button } from "../components";
import { ArrowLeft, Upload, Mic, Check, AlertCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const VoiceUploadPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [voiceName, setVoiceName] = useState("");
  const [audioFiles, setAudioFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAudioFiles(files);
    setError(null);
    console.log("Files selected:", files.map(f => `${f.name} (${f.size} bytes)`));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!voiceName.trim()) {
      setError("Please enter a voice name");
      return;
    }
    
    if (audioFiles.length === 0) {
      setError("Please select audio files");
      return;
    }

    setIsUploading(true);
    setProgress("Preparing files...");
    setError(null);

    try {
      const formData = new FormData();
      formData.append("voice_name", voiceName);
      
      // Add each file
      let totalSize = 0;
      for (const file of audioFiles) {
        console.log(`Adding: ${file.name}, ${file.size} bytes`);
        totalSize += file.size;
        formData.append("audio_files", file);
      }
      console.log(`Total: ${(totalSize/1024/1024).toFixed(2)} MB`);

      setProgress(`Uploading ${(totalSize/1024/1024).toFixed(1)} MB...`);
      
      // Use axios which has the auth token pre-configured
      const response = await axios.post(`${API_URL}/api/voices/clone`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000 // 2 minute timeout for large files
      });

      setProgress("Processing voice...");
      console.log("Response:", response.data);

      setSuccess(true);
      setProgress("Voice created successfully!");
      
    } catch (err) {
      console.error("Upload error:", err);
      if (err.response?.status === 401) {
        setError("Session expired. Please log out and log back in.");
      } else {
        setError(err.response?.data?.detail || err.message || "Upload failed");
      }
      setProgress("");
    } finally {
      setIsUploading(false);
    }
  };

  if (success) {
    return (
      <PageWrapper>
        <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center p-6">
          <Card className="max-w-sm w-full p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="text-green-600" size={32} />
            </div>
            <h2 className="font-heading text-2xl text-[#1F2937] mb-2">Voice Created!</h2>
            <p className="text-[#6B7280] mb-6">Your voice clone is ready to use.</p>
            <Button onClick={() => navigate("/account/voice-settings")} className="w-full">
              View My Voices
            </Button>
          </Card>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper showNav={false}>
      <div className="min-h-screen bg-[#FAFAF7]">
        {/* Header */}
        <div className="bg-[#2D4A2A] text-white px-6 py-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/80">
            <ArrowLeft size={20} />
            Back
          </button>
          <h1 className="font-heading text-2xl mt-4">Upload Voice</h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-w-md mx-auto">
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
              <AlertCircle size={20} />
              {error}
            </div>
          )}

          {/* Voice Name */}
          <div>
            <label className="block text-sm font-medium text-[#1F2937] mb-2">
              Voice Name *
            </label>
            <input
              type="text"
              value={voiceName}
              onChange={(e) => setVoiceName(e.target.value)}
              placeholder="My Voice"
              className="w-full px-4 py-3 border border-[#E5E7EB] rounded-xl"
              disabled={isUploading}
            />
          </div>

          {/* File Input */}
          <div>
            <label className="block text-sm font-medium text-[#1F2937] mb-2">
              Audio Files *
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*,.mp3,.wav,.m4a,.aac"
              multiple
              onChange={handleFileChange}
              className="w-full p-3 border border-[#E5E7EB] rounded-xl bg-white"
              disabled={isUploading}
            />
            <p className="text-xs text-[#9CA3AF] mt-1">
              Select MP3, WAV, M4A, or AAC files
            </p>
          </div>

          {/* Selected Files */}
          {audioFiles.length > 0 && (
            <div className="bg-[#F3F4F6] rounded-xl p-4">
              <p className="text-sm font-medium text-[#1F2937] mb-2">
                {audioFiles.length} file(s) selected:
              </p>
              <ul className="text-sm text-[#6B7280] space-y-1">
                {audioFiles.map((f, i) => (
                  <li key={i}>• {f.name} ({(f.size / 1024 / 1024).toFixed(2)} MB)</li>
                ))}
              </ul>
            </div>
          )}

          {/* Progress */}
          {progress && (
            <div className="bg-[#4A6741]/10 rounded-xl p-4 text-center">
              <div className="w-6 h-6 border-2 border-[#4A6741] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-sm text-[#4A6741]">{progress}</p>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isUploading || !voiceName.trim() || audioFiles.length === 0}
            className="w-full py-4"
          >
            {isUploading ? "Creating Voice..." : "Create Voice Clone"}
          </Button>

          <p className="text-xs text-center text-[#9CA3AF]">
            Upload at least 30 seconds of clear audio for best results.
          </p>
        </form>
      </div>
    </PageWrapper>
  );
};

export default VoiceUploadPage;
